import {
  fullStoryContentCatalog,
  hasFullStoryContent
} from './fullStoryContentManifest.ts';

export type FairyTaleAge = '4–6' | '7–10';
export type FairyTaleCategory = 'Bajke' | 'Životinje' | 'Pred spavanje';

export type FairyTaleSource = {
  author: string;
  work: string;
  url: string;
  publicDomain: true;
};

export type FairyTale = {
  id: string;
  age: FairyTaleAge;
  category: FairyTaleCategory;
  title: string;
  art: string;
  pages: string[][];
  sentences: string[];
  question: string;
  answers: string[];
  correct: string;
  audioKey: string;
  recordedAudio: boolean;
  recordedVoice: 'sr-RS-SophieNeural';
  plotKey: string;
  source: FairyTaleSource;
  adaptation: 'originalna-srpska-adaptacija';
  edition: 'complete' | 'abridged';
  narration: 'audio-first';
  reviewed: boolean;
  fullContentAvailable: boolean;
};

type ClassicTaleSeed = {
  id: string;
  title: string;
  art: string;
  category: FairyTaleCategory;
  source: FairyTaleSource;
  plot: [string, string, string, string, string, string, string, string, string, string];
  lesson: string;
  question: string;
  answers: [string, string, string];
  correct: string;
  recordedAudio?: boolean;
};

const grimm: FairyTaleSource = {
  author: 'Braća Grim',
  work: 'Grimms’ Fairy Tales',
  url: 'https://www.gutenberg.org/ebooks/2591',
  publicDomain: true
};

const andersen: FairyTaleSource = {
  author: 'Hans Kristijan Andersen',
  work: 'Andersen’s Fairy Tales',
  url: 'https://www.gutenberg.org/ebooks/1597',
  publicDomain: true
};

const perrault: FairyTaleSource = {
  author: 'Šarl Pero',
  work: 'The Fairy Tales of Charles Perrault',
  url: 'https://www.gutenberg.org/ebooks/29021',
  publicDomain: true
};

const jacobs: FairyTaleSource = {
  author: 'Džozef Džejkobs',
  work: 'English Fairy Tales',
  url: 'https://www.gutenberg.org/ebooks/7439',
  publicDomain: true
};

const collodi: FairyTaleSource = {
  author: 'Karlo Kolodi',
  work: 'The Adventures of Pinocchio',
  url: 'https://www.gutenberg.org/ebooks/500',
  publicDomain: true
};

const goethe: FairyTaleSource = {
  author: 'Johan Volfgang fon Gete',
  work: 'The Poems of Goethe — The Sorcerer’s Apprentice',
  url: 'https://www.gutenberg.org/ebooks/1287',
  publicDomain: true
};

const pushkin: FairyTaleSource = {
  author: 'Aleksandar Sergejevič Puškin',
  work: 'Bajka o ribaru i ribici',
  url: 'https://ru.wikisource.org/wiki/Сказка_о_рыбаке_и_рыбке_(Пушкин)',
  publicDomain: true
};

const browning: FairyTaleSource = {
  author: 'Robert Brauning',
  work: 'The Pied Piper of Hamelin',
  url: 'https://www.gutenberg.org/ebooks/18343',
  publicDomain: true
};

// Tekstovi ispod nisu preuzeti prevodi. To su nove, uzrasno prilagođene
// adaptacije zasnovane na zapletima javnodomenskih originala.
const classics: ClassicTaleSeed[] = [
  {
    id: 'ivica-i-marica',
    title: 'Ивица и Марица',
    art: '🏡 🍬 🌲',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Ивица и Марица живели су скромно са оцем дрвосечом на крају велике шуме.',
      'Када су се изгубили, Ивица је остављао беле каменчиће да запамте пут до куће.',
      'Другог пута птице су појеле мрвице хлеба, па су деца остала дубоко међу дрвећем.',
      'На пропланку су открили кућицу од медењака, шећера и мирисних колача.',
      'Љубазни глас из куће припадао је лукавој вештици која је намеравала да их зароби.',
      'Марица је сваког дана храбрила брата и пажљиво посматрала где вештица држи кључ.',
      'Када је дошла прилика, деца су надмудрила вештицу и ослободила се њене замке.',
      'У кући су пронашли драгуље које је вештица годинама крила од путника.',
      'Пратећи белу патку преко воде, Ивица и Марица нашли су стазу ка свом дому.',
      'Отац их је загрлио, а породица је од тада живела заједно, сигурно и сложно.'
    ],
    lesson: 'слога, прибраност и храброст помажу и када пут делује изгубљено',
    question: 'Како су Ивица и Марица први пут обележили пут?',
    answers: ['Белим каменчићима', 'Плавим тракама', 'Златним звонцима'],
    correct: 'Белим каменчићима'
  },
  {
    id: 'pinokio',
    title: 'Пинокио',
    art: '🤥 🪵 🦗',
    category: 'Bajke',
    source: collodi,
    plot: [
      'Стари мајстор Ђепето направио је дрвеног лутка и назвао га Пинокио.',
      'На његово запрепашћење, лутак је оживео, заплесао по радионици и пожелео да упозна свет.',
      'Мудри Цврчак покушао је да га научи да слобода увек носи и одговорност.',
      'Пинокио је продао буквар и отишао у позориште лутака уместо у школу.',
      'Лисица и Мачак обећали су му лако богатство, а затим покушали да му узму новчиће.',
      'Добра Вила помогла му је, али му је нос растао кад год би говорио неистину.',
      'У Земљи играчака Пинокио је схватио да забава без мере може донети велике невоље.',
      'Трагајући за Ђепетом, доспео је у утробу огромне морске немани.',
      'Пинокио је смислио бекство, спасао оца и вредно радио да исправи своје грешке.',
      'Због искрености, храбрости и љубави према оцу, постао је прави дечак.'
    ],
    lesson: 'истина, учење и одговорност помажу нам да постанемо бољи',
    question: 'Шта се дешавало када Пинокио слаже?',
    answers: ['Растао му је нос', 'Плавела му је коса', 'Нестајале су му ципеле'],
    correct: 'Растао му је нос'
  },
  {
    id: 'crvenkapa',
    title: 'Црвенкапа',
    art: '🧺 🐺 🌲',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Девојчица са црвеном капицом кренула је кроз шуму да однесе баки хлеб и колач.',
      'Мама јој је рекла да не скреће са стазе и да не разговара са непознатима.',
      'Лукави вук сазнао је куда иде и наговорио је да бере цвеће поред пута.',
      'Док је Црвенкапа правила букет, вук је пожурио до бакине куће.',
      'Прерушен у баку, сакрио се у кревету и покушао да превари девојчицу.',
      'Црвенкапи су били чудни велики уши, очи и зуби наводне баке.',
      'Шумар је чуо буку, ушао у кућу и безбедно избавио баку и унуку.',
      'Бака је закључала врата, а вук је побегао далеко у шуму.',
      'Црвенкапа је признала да није послушала мамин савет и обећала да ће бити опрезнија.',
      'Сви су заједно поделили колач, а девојчица више није напуштала сигурну стазу.'
    ],
    lesson: 'правила безбедности постоје да нас заштите',
    question: 'Коме је Црвенкапа носила корпу?',
    answers: ['Баки', 'Учитељици', 'Принцези'],
    correct: 'Баки'
  },
  {
    id: 'tri-praseta',
    title: 'Три прасета',
    art: '🐷 🧱 🐺',
    category: 'Životinje',
    source: jacobs,
    plot: [
      'Три прасета су одрасла и свако је решило да сагради сопствену кућу.',
      'Прво прасе брзо је направило кућу од сламе да би имало више времена за игру.',
      'Друго је изабрало пруће, а треће је стрпљиво зидало циглу по циглу.',
      'Вук је дувањем срушио кућу од сламе, па је прво прасе побегло код брата.',
      'Срушио је и кућу од прућа, а оба прасета потрчала су у кућу од цигле.',
      'Колико год је дувао, чврсти зидови нису се ни померили.',
      'Вук је покушао да уђе кроз димњак, али су прасићи чули његове кораке.',
      'Запалили су ватру и поставили велики лонац, па се вук уплашио и побегао.',
      'Три брата су затим заједно поправила двориште и направила сигурне собе за све.',
      'Научили су да пажљив рад траје дуже, али доноси поуздан и миран дом.'
    ],
    lesson: 'труд и стрпљење граде оно што невоља не може лако да сруши',
    question: 'Од чега је била најчвршћа кућа?',
    answers: ['Од цигле', 'Од сламе', 'Од папира'],
    correct: 'Од цигле'
  },
  {
    id: 'pepeljuga',
    title: 'Пепељуга',
    art: '👠 🎃 ✨',
    category: 'Bajke',
    source: perrault,
    plot: [
      'Добра девојка Пепељуга живела је са маћехом и сестрама које су јој давале све кућне послове.',
      'Када је стигао позив за краљевски бал, сестре су јој забраниле да пође.',
      'Добра вила претворила је бундеву у кочију, мишеве у коње и стару хаљину у свечану.',
      'Чаролија је могла трајати само до поноћи, па је Пепељуга обећала да ће се вратити на време.',
      'На балу је принц плесао само са непознатом девојком у стакленим ципелицама.',
      'Када је сат почео да откуцава поноћ, Пепељуга је потрчала и изгубила једну ципелицу.',
      'Принц је обишао краљевство тражећи девојку којој мала ципела савршено пристаје.',
      'Маћеха је покушала да је сакрије, али су птице помогле да Пепељуга буде пронађена.',
      'Стаклена ципелица пристајала је само њој, а принц је препознао своју плесну сапутницу.',
      'Пепељуга је започела нов живот, али није заборавила да су доброта и достојанство њена права чаролија.'
    ],
    lesson: 'доброта и достојанство вреде више од скупоцене одеће',
    question: 'Шта је Пепељуга изгубила бежећи са бала?',
    answers: ['Стаклену ципелицу', 'Златну круну', 'Плаву рукавицу'],
    correct: 'Стаклену ципелицу'
  },
  {
    id: 'snezana',
    title: 'Снежана и седам патуљака',
    art: '🍎 👑 ⛏️',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Снежана је била добра принцеза, а сујетна краљица завидела је њеној лепоти.',
      'Краљичино огледало рекло је истину, па је она наредила ловцу да одведе Снежану у шуму.',
      'Ловац је пустио девојку, која је после дугог лутања нашла малу кућу.',
      'У кући је живело седам патуљака који су радили у руднику и прихватили је као пријатељицу.',
      'Прерушена краљица три пута је покушала да превари Снежану.',
      'Последњи пут понудила јој је зачарану јабуку од које је девојка утонула у дубок сан.',
      'Патуљци су чували Снежану у стакленом ковчегу окруженом шумским цвећем.',
      'Млади принц је затражио да је пренесу на безбедно место, а комадић јабуке се ослободио.',
      'Снежана се пробудила, захвалила патуљцима и одлучила да више не верује непознатим продавцима.',
      'Краљичина завист изгубила је моћ, док су пријатељство и истина донели мир краљевству.'
    ],
    lesson: 'завист рањава, а верни пријатељи и истина чувају',
    question: 'Колико је патуљака живело у кући?',
    answers: ['Седам', 'Три', 'Дванаест'],
    correct: 'Седам'
  },
  {
    id: 'ruzno-pace',
    title: 'Ружно паче',
    art: '🦢 🥚 🌾',
    category: 'Životinje',
    source: andersen,
    plot: [
      'У пачјем гнезду последње се излегло велико сиво паче које није личило на остале.',
      'Животиње су га задиркивале, па је тужно напустило двориште тражећи место где ће бити прихваћено.',
      'Срело је дивље патке и гуске, али је изненадна опасност растерала цело јато.',
      'Старица му је дала склониште, мада мачак и кокошка нису разумели његову жељу за водом.',
      'Паче је наставило пут и први пут угледало величанствене беле лабудове.',
      'Током хладне зиме једва је преживело на залеђеном језеру.',
      'Добри сељак га је спасао, али се уплашено паче поново вратило природи.',
      'Када је стигло пролеће, одважило се да приђе лабудовима и очекивало одбацивање.',
      'У одразу воде видело је да је и само постало леп, снажан лабуд.',
      'Схватило је да различитост није мана и да одрастање понекад тражи време.'
    ],
    lesson: 'не треба судити о некоме по изгледу нити пре него што одрасте',
    question: 'У коју птицу је одрасло сиво паче?',
    answers: ['У лабуда', 'У орла', 'У пауна'],
    correct: 'У лабуда'
  },
  {
    id: 'carevo-novo-odelo',
    title: 'Царево ново одело',
    art: '👑 🧵 😮',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Један цар више је бринуо о новој одећи него о пословима свога краљевства.',
      'Двојица варалица представила су се као ткачи који умеју да направе чудесно невидљиво платно.',
      'Тврдили су да тканину не могу видети људи који нису мудри или достојни свог положаја.',
      'Министри нису видели ништа, али су из страха хвалили боје и шаре празног разбоја.',
      'Ни цар није видео тканину, ипак се претварао да је одушевљен.',
      'Варалице су глумиле да кроје, секу и облаче цара у непостојеће свечано одело.',
      'Током поворке сви грађани су ћутали јер нико није желео да изгледа неразумно.',
      'Једно дете је искрено узвикнуло да цар уопште нема одећу.',
      'Људи су прихватили очигледну истину, а цар је схватио колико га је сујета заслепила.',
      'Од тог дана више је слушао искрене савете него ласкање и празне похвале.'
    ],
    lesson: 'истина остаје истина чак и када се сви плаше да је изговоре',
    question: 'Ко је први гласно рекао истину?',
    answers: ['Једно дете', 'Главни министар', 'Дворски кројач'],
    correct: 'Једно дете'
  },
  {
    id: 'rapunzel',
    title: 'Рапунзел',
    art: '🗼 👱‍♀️ 🌿',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Вештица је одвела девојчицу Рапунзел и одгајала је далеко од других људи.',
      'Када је Рапунзел порасла, затворила ју је у високу кулу без врата и степеница.',
      'Вештица се пењала тако што би позвала девојку да спусти своју веома дугу косу.',
      'Принц је чуо Рапунзелину песму и открио тајни начин уласка у кулу.',
      'Двоје младих постали су пријатељи и смислили план да Рапунзел напусти заточеништво.',
      'Вештица је открила план, одсекла косу и одвела девојку у далеку пустош.',
      'Принц је пао са куле и дуго лутао, не одустајући од потраге.',
      'После много времена поново је чуо познати глас и пронашао Рапунзел.',
      'Њене сузе радоснице излечиле су му повређене очи.',
      'Заједно су стигли у његово краљевство и више нико није одлучивао уместо Рапунзел.'
    ],
    lesson: 'нада и истрајност могу пронаћи пут и из највише куле',
    question: 'Како се вештица пењала у кулу?',
    answers: ['Уз Рапунзелину косу', 'Летећим тепихом', 'Златним степеницама'],
    correct: 'Уз Рапунзелину косу'
  },
  {
    id: 'uspavana-lepotica',
    title: 'Успавана лепотица',
    art: '🌹 🏰 😴',
    category: 'Pred spavanje',
    source: perrault,
    plot: [
      'На прославу рођења принцезе позване су добре виле да јој дарују племените особине.',
      'Увређена вила коју су заборавили предвидела је да ће се принцеза убости на вретено.',
      'Најмлађа добра вила ублажила је клетву: уместо смрти, девојка ће спавати сто година.',
      'Краљ је забранио вретена, али је радознала принцеза једно ипак пронашла у старој кули.',
      'Чим је додирнула оштар врх, заспала је, а са њом и цео дворац.',
      'Око зидина израсла је густа шума трња која је годинама чувала уснуле становнике.',
      'После сто година млади принц чуо је причу и кренуо ка заборављеном замку.',
      'Трње се претворило у цветне гране, а капије су се саме отвориле.',
      'Када је принцеза отворила очи, пробудили су се кувари, стражари, коњи и дворјани.',
      'Дворац је поново испунила музика, а дуго стрпљење завршило се радосним јутром.'
    ],
    lesson: 'и после најдуже ноћи може стићи ново јутро',
    question: 'Колико година је дворац спавао?',
    answers: ['Сто година', 'Седам дана', 'Једну зиму'],
    correct: 'Сто година'
  },
  {
    id: 'mala-sirena',
    title: 'Мала сирена',
    art: '🧜‍♀️ 🌊 🐚',
    category: 'Bajke',
    source: andersen,
    plot: [
      'На дну мора живела је најмлађа сирена која је сањала да упозна свет изнад таласа.',
      'Када је напунила петнаест година, испливала је и угледала брод младог принца.',
      'Олуја је разбила брод, а сирена је спасла принца и оставила га безбедног на обали.',
      'Желећи људски живот, посетила је морску вештицу и дала свој глас за ноге.',
      'Сваки корак био је тежак, али је сирена храбро боравила на двору поред принца.',
      'Принц није знао ко га је спасао и оженио је девојку за коју је веровао да му је помогла.',
      'Сестре су сирени понудиле начин да се врати у море, али би тиме повредила принца.',
      'Она је изабрала милосрђе и бацила зачарани нож у таласе.',
      'Уместо да нестане, постала је дух ваздуха који добрим делима стиче бесмртну душу.',
      'Њена прича памти се као прича о несебичној љубави, избору и великој храбрости.'
    ],
    lesson: 'права љубав не тражи да други страдају због наших жеља',
    question: 'Шта је сирена дала морској вештици?',
    answers: ['Свој глас', 'Своју круну', 'Своју шкољку'],
    correct: 'Свој глас'
  },
  {
    id: 'mackak-u-cizmama',
    title: 'Мачак у чизмама',
    art: '🐈 👢 👑',
    category: 'Životinje',
    source: perrault,
    plot: [
      'Најмлађем млинаревом сину у наследство је припао само један наизглед обичан мачак.',
      'Мачак је затражио чизме и врећу, обећавајући да ће показати колико заиста вреди.',
      'Хватао је дивљач и носио је краљу као поклон од измишљеног маркиза Карабаса.',
      'Када је краљева кочија пролазила крај реке, мачак је смислио лукав план са господаревом одећом.',
      'Краљ је дао младићу свечано рухо и повео га у кочију поред принцезе.',
      'Мачак је замолио сељаке да кажу да њиве припадају маркизу Карабасу.',
      'Затим је стигао у дворац људождера који је умео да се претвара у животиње.',
      'Наговорио га је да покаже како постаје мали миш, а онда га брзо ухватио.',
      'Краљ је био одушевљен дворцем и поверио је младићу руку принцезе.',
      'Мачак је остао цењен саветник, доказавши да домишљатост може променити нечију судбину.'
    ],
    lesson: 'сналажљивост вреди када се користи да помогне, а не да повреди',
    question: 'Шта је мачак најпре затражио?',
    answers: ['Чизме и врећу', 'Круну и мач', 'Чамац и весло'],
    correct: 'Чизме и врећу'
  },
  {
    id: 'princeza-na-zrnu-graska',
    title: 'Принцеза на зрну грашка',
    art: '👸 🛏️ 🟢',
    category: 'Pred spavanje',
    source: andersen,
    plot: [
      'Млади принц путовао је светом тражећи праву принцезу, али није био сигуран кога је упознао.',
      'Једне олујне ноћи на капију је покуцала мокра и промрзла девојка.',
      'Рекла је да је принцеза, иако њена одећа после кише није изгледала краљевски.',
      'Краљица је одлучила да провери њену осетљивост необичним, али безопасним тестом.',
      'На дно кревета ставила је једно мало зрно грашка.',
      'Преко њега је поређала двадесет душека и још толико меких перина.',
      'Гошћа је целе ноћи покушавала да заспи, али јој је нешто тврдо сметало.',
      'Ујутру је искрено рекла да је од притиска добила модрицу.',
      'Краљевска породица закључила је да само веома осетљива принцеза може осетити зрно.',
      'Принц је коначно нашао сапутницу, а зрно грашка сачувано је у музеју.'
    ],
    lesson: 'спољашњи изглед не говори увек ко је неко заиста',
    question: 'Шта је било сакривено испод душека?',
    answers: ['Зрно грашка', 'Златни кључ', 'Мала круна'],
    correct: 'Зрно грашка'
  },
  {
    id: 'dzek-i-pasulj',
    title: 'Џек и чаробни пасуљ',
    art: '🌱 🏰 🐄',
    category: 'Bajke',
    source: jacobs,
    plot: [
      'Сиромашни Џек одвео је краву на пијацу, али ју је заменио за неколико необичних зрна пасуља.',
      'Његова мајка бацила је зрна кроз прозор, а преко ноћи израсла је стабљика до облака.',
      'Џек се попео и пронашао огроман дворац у коме је живео страшни џин.',
      'Џинова жена сакрила је дечака и дала му мало хране.',
      'Џек је открио да џин чува злато које је некада припадало његовој породици.',
      'Вратио је кесу новчића, а касније и кокошку која носи златна јаја.',
      'Трећи пут узео је чаробну харфу, али је она гласно позвала свог господара.',
      'Џек је потрчао низ стабљику док се џин спуштао одмах иза њега.',
      'На земљи је секиром пресекао стабљику, па се џин више није могао вратити.',
      'Џек и мајка пажљиво су користили враћено богатство и више нису гладовали.'
    ],
    lesson: 'храброст мора ићи заједно са одговорношћу',
    question: 'Шта је израсло из чаробног пасуља?',
    answers: ['Стабљика до облака', 'Златна шума', 'Кућа од колача'],
    correct: 'Стабљика до облака'
  },
  {
    id: 'zlatokosa-i-tri-medveda',
    title: 'Златокоса и три медведа',
    art: '👧 🐻 🥣',
    category: 'Životinje',
    source: jacobs,
    plot: [
      'Златокоса је шетајући шумом угледала кућицу и покуцала, али нико није одговорио.',
      'Ушла је без дозволе и на столу пронашла три чиније каше.',
      'Прва је била преврућа, друга прехладна, а каша малог медведа баш како треба.',
      'Испробала је три столице и случајно поломила најмању.',
      'На спрату су је чекала три кревета различите величине.',
      'Заспала је у кревету малог медведа јер јој је једино он био удобан.',
      'Када се породица медведа вратила, приметила је кашу, столице и трагове по кући.',
      'Мали медвед пронашао је девојчицу у свом кревету и позвао родитеље.',
      'Златокоса се пробудила, извинила се и обећала да више неће улазити у туђ дом.',
      'Заједно су поправили столицу, а медведи су прихватили њено искрено извињење.'
    ],
    lesson: 'туђе ствари и дом поштујемо и увек прво тражимо дозволу',
    question: 'Чија каша је Златокоси одговарала?',
    answers: ['Каша малог медведа', 'Каша тате медведа', 'Каша маме медведице'],
    correct: 'Каша малог медведа'
  },
  {
    id: 'palcica',
    title: 'Палчица',
    art: '🌷 🧚‍♀️ 🐭',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Из чаробног цвета родила се девојчица Палчица, мала колико људски палац.',
      'Једне ноћи жаба ју је однела, желећи да је уда за свог сина.',
      'Рибице су прегризле лист на коме је била заробљена и пустиле је низ поток.',
      'После лета у природи, Палчица је током зиме нашла уточиште код пољског миша.',
      'Миш је желео да се уда за богатог крта који није волео сунце ни цвеће.',
      'У подземном ходнику Палчица је пронашла промрзлу ласту и тајно је неговала.',
      'Када је дошло пролеће, излечена ласта понудила јој је да одлете у топле крајеве.',
      'Палчица је напустила мрачне тунеле и стигла до земље пуне цветова.',
      'У једном цвету упознала је малог краља вила који је поштовао њене жеље.',
      'Добила је крила и ново име Маја, али је заувек остала пријатељица добре ласте.'
    ],
    lesson: 'помоћ коју пружимо може нам једног дана отворити пут ка слободи',
    question: 'Ко је Палчицу однео у топле крајеве?',
    answers: ['Ласта', 'Жаба', 'Крт'],
    correct: 'Ласта'
  },
  {
    id: 'zablji-kralj',
    title: 'Жабљи краљ',
    art: '🐸 👑 ⚽',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Принцези је омиљена златна лопта упала у дубок шумски бунар.',
      'Жаба је понудила да је извади ако јој принцеза дозволи да буде њен пријатељ.',
      'Девојка је брзо обећала, узела лопту и одјурила не испунивши реч.',
      'Следећег дана жаба је дошла на двор и затражила оно што јој је обећано.',
      'Краљ је подсетио ћерку да часна особа мора поштовати дату реч.',
      'Принцеза је невољно поделила вечеру и дозволила жаби да остане у соби.',
      'Када је коначно престала да је одбацује, стара чаролија је почела да нестаје.',
      'Жаба се претворила у принца кога је зла вештица давно зачарала.',
      'Верни слуга Хајнрих довезао је кочију и радосно дочекао ослобођеног господара.',
      'Принцеза је научила да пријатељство почиње поштовањем и испуњеним обећањем.'
    ],
    lesson: 'обећање важи и онда када нам више није згодно',
    question: 'Шта је жаба извадила из бунара?',
    answers: ['Златну лопту', 'Сребрну круну', 'Црвену јабуку'],
    correct: 'Златну лопту'
  },
  {
    id: 'bremenski-muzikanti',
    title: 'Бременски музиканти',
    art: '🐴 🐕 🐈 🐓',
    category: 'Životinje',
    source: grimm,
    plot: [
      'Остарели магарац напустио је газду и кренуо у Бремен да постане музичар.',
      'На путу су му се придружили пас, мачка и петао којима такође више нису веровали.',
      'Четири пријатеља увежбавала су необичан хор и храбрила једно друго.',
      'Када је пао мрак, угледали су светло у кући усред шуме.',
      'Кроз прозор су видели разбојнике за богатом трпезом.',
      'Магарац, пас, мачка и петао попели су се један на другог и запевали из свег гласа.',
      'Разбојници су помислили да их напада страшно чудовиште и побегли у шуму.',
      'Један се вратио, али су га животиње у мраку толико уплашиле да је поново одјурио.',
      'Пријатељи су очистили кућу, поделили храну и свако пронашао удобно место.',
      'Толико им се допао заједнички дом да никада нису ни стигли до Бремена.'
    ],
    lesson: 'пријатељи различитих способности заједно могу створити нови дом',
    question: 'У који град су животиње првобитно кренуле?',
    answers: ['У Бремен', 'У Париз', 'У Рим'],
    correct: 'У Бремен'
  },
  {
    id: 'vuk-i-sedam-jarica',
    title: 'Вук и седам јарића',
    art: '🐐 🐺 🕰️',
    category: 'Životinje',
    source: grimm,
    plot: [
      'Мама коза оставила је седам јарића код куће и упозорила их на вуков груб глас и црне шапе.',
      'Вук је прво покуцао дубоким гласом, па су јарићи одмах одбили да отворе.',
      'Појео је креду да омекша глас, али су деца кроз прозор видела тамну шапу.',
      'Затим је шапу посуо белим брашном и коначно их преварио.',
      'Јарићи су се сакрили у орман, кревет, пећ, кухињу, умиваоник, сто и велики сат.',
      'Вук је пронашао шесторо, али најмлађе јаре у сату није приметио.',
      'Када се мама вратила, најмлађе јаре испричало јој је све што се догодило.',
      'Пронашли су вука како спава и безбедно ослободили јариће из његовог великог стомака.',
      'Уместо њих ставили су тешко камење, па вук више није могао да их јури.',
      'Породица се вратила кући и јарићи никада више нису отворили врата непознатом.'
    ],
    lesson: 'не отварамо непознатима и пажљиво проверавамо ко је пред вратима',
    question: 'Где се сакрило најмлађе јаре?',
    answers: ['У великом сату', 'Испод моста', 'У корпи'],
    correct: 'У великом сату'
  },
  {
    id: 'hrabri-krojac',
    title: 'Храбри кројач',
    art: '🧵 💪 👑',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Млади кројач је једним ударцем убио седам мува које су слетеле на његов пекмез.',
      'Направио је појас са натписом „Седам једним ударцем” и кренуо да упозна свет.',
      'Џин је помислио да натпис говори о седам људи и изазвао га на испит снаге.',
      'Кројач је уместо камена стиснуо сир и пустио сок, а птицу је бацио високо као да је камен.',
      'Користећи памет, измакао је замкама које су му припремали џинови.',
      'Краљ му је задао да савлада два опасна џина, једнорога и дивљу свињу.',
      'Кројач је натерао џинове да се међусобно сукобе, а једнорога да забоде рог у дрво.',
      'Дивљу свињу је затворио у напуштену капелу без борбе.',
      'Краљ је морао да одржи обећање и да храбром кројачу део краљевства.',
      'Тако је младић показао да бистра глава понекад вреди више од великих мишића.'
    ],
    lesson: 'сналажљивост и самопоуздање могу надокнадити недостатак снаге',
    question: 'Шта је кројач заиста савладао једним ударцем?',
    answers: ['Седам мува', 'Седам џинова', 'Седам лавова'],
    correct: 'Седам мува'
  },
  {
    id: 'zlatna-guska',
    title: 'Златна гуска',
    art: '🪿 ✨ 🌳',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Најмлађег брата сви су звали Глупко, иако је био једини који је радо делио храну.',
      'Захвални старац показао му је старо дрво у чијем корењу је лежала златна гуска.',
      'Три радознале сестре покушале су да украду златно перо и залепиле се за птицу.',
      'Док је Глупко ходао, за њима су се редом лепили свештеник, звонар и радници.',
      'Необична поворка стигла је пред принцезу која се годинама није насмејала.',
      'Када их је угледала, принцеза се коначно насмејала од срца.',
      'Краљ ипак није желео да одржи обећање, па је Глупку задао нове тешке задатке.',
      'Уз помоћ старчевих пријатеља, младић је пронашао човека који може попити језеро и појести брдо хлеба.',
      'На крају је довезао чудесан брод који плови и копном и водом.',
      'Краљ је признао његову доброту и истрајност, а принцеза је сама прихватила пријатељство.'
    ],
    lesson: 'љубазност према некоме кога други потцењују може отворити неочекивана врата',
    question: 'Шта је насмејало принцезу?',
    answers: ['Необична поворка', 'Краљева песма', 'Златна круна'],
    correct: 'Необична поворка'
  },
  {
    id: 'postojani-limeni-vojnik',
    title: 'Постојани лимени војник',
    art: '🪖 🩰 🐟',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Међу двадесет пет лимених војника један је имао само једну ногу, јер је последњи изливен.',
      'На столу је угледао папирну балерину која је стајала на једној нози и одмах му се допала.',
      'Љубоморни играчки трол упозорио га је да не гледа балерину.',
      'Следећег јутра војник је пао кроз прозор на улицу пуну кише.',
      'Дечаци су га ставили у папирни чамац који је отпловио низ олук.',
      'После сусрета са воденим пацовом, чамац је потонуо, а војника је прогутала риба.',
      'Риба је ухваћена и донета баш у кућу из које је војник нестао.',
      'Куварица га је пронашла, па се поново нашао на столу преко пута балерине.',
      'Изненада је завршио у ватри, а ветар је поред њега донео и папирну балерину.',
      'У пепелу је остало мало лимено срце као знак његове постојаности.'
    ],
    lesson: 'достојанство и оданост не зависе од тога колико је пут лак',
    question: 'Шта је остало у пепелу?',
    answers: ['Лимено срце', 'Златни мач', 'Папирни брод'],
    correct: 'Лимено срце'
  },
  {
    id: 'slavuj',
    title: 'Славуј',
    art: '🐦 🎶 🏯',
    category: 'Životinje',
    source: andersen,
    plot: [
      'Кинески цар живео је у раскошној палати, али није знао да у његовој башти пева дивни славуј.',
      'Путници су више хвалили птичију песму него царев порцелански дворац.',
      'Куварица је пронашла малог сивог славуја и довела га пред двор.',
      'Његова природна песма дирнула је цара до суза.',
      'Убрзо је стигла механичка птица украшена драгуљима која је увек понављала исту мелодију.',
      'Дворани су заборавили правог славуја, па је он одлетео назад у шуму.',
      'Механичка птица се покварила, а цар се после неколико година тешко разболео.',
      'Прави славуј чуо је вест, вратио се и песмом отерао мрачне сенке из собе.',
      'Цар је понудио награду, али је птица тражила само слободу да долази и одлази.',
      'Од тада је славуј певао искрено, а цар је научио да жива лепота нема дугме за понављање.'
    ],
    lesson: 'искрена уметност и пријатељство вреде више од сјајне имитације',
    question: 'Шта је излечило цара?',
    answers: ['Песма правог славуја', 'Златна круна', 'Механички сат'],
    correct: 'Песма правог славуја'
  },
  {
    id: 'snezna-kraljica',
    title: 'Снежна краљица',
    art: '❄️ 👑 🦌',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Пријатељи Герда и Кај гајили су руже и слушали бакине приче о Снежној краљици.',
      'Комадић чаробног огледала ушао је Кају у око и срце, па је постао хладан и груб.',
      'Снежна краљица одвела га је санкама у своју ледену палату.',
      'Герда је кренула у потрагу и путем срела реку, чаробницу и гавране.',
      'Принц и принцеза дали су јој топлу одећу и златну кочију.',
      'Разбојничка девојчица ослободила је Герду и позајмила јој северног ирваса.',
      'Лапонка и Финка помогле су јој да стигне до краљичиног замка.',
      'Герда је пронашла Каја како од ледених комада покушава да сложи реч вечност.',
      'Њене топле сузе отопиле су лед у његовом срцу и избациле комадић огледала.',
      'Деца су се вратила кући и видела да су одрасла, док су руже поново цветале.'
    ],
    lesson: 'истрајна љубав и топлина могу отопити и најхладније срце',
    question: 'Ко је кренуо да пронађе Каја?',
    answers: ['Герда', 'Разбојник', 'Краљица'],
    correct: 'Герда'
  },
  {
    id: 'carobni-lonac',
    title: 'Чаробни лонац',
    art: '🥣 ✨ 🏘️',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Сиромашна девојчица поделила је последњи комад хлеба са старицом у шуми.',
      'Старица јој је поклонила мали лонац који кува слатку кашу на праве речи.',
      'Када би рекла „Лончићу, кувај”, мирисна каша сама би испунила посуду.',
      'Речи „Лончићу, стани” заустављале су чаролију тачно на време.',
      'Девојчица и мајка више нису биле гладне и делиле су кашу са суседима.',
      'Једног дана мајка је покренула лонац док је девојчица била ван куће.',
      'Заборавила је речи за заустављање, па је каша испунила кухињу и улицу.',
      'Комшије су се попеле на столове, ограде и кровове да остану суве.',
      'Девојчица се вратила, изговорила праве речи и лонац је коначно стао.',
      'Људи су појели пут кроз кашу, а сви су од тада пажљиво учили цело упутство.'
    ],
    lesson: 'користан поклон тражи да пажљиво научимо како се употребљава',
    question: 'Шта је кувао чаробни лонац?',
    answers: ['Слатку кашу', 'Златне новчиће', 'Плаву боју'],
    correct: 'Слатку кашу'
  },
  {
    id: 'sest-labudova',
    title: 'Шест лабудова',
    art: '🦢 👑 🧶',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Зла чаробница претворила је шест младих принчева у беле лабудове.',
      'Њихова сестра сазнала је да чаролију може разбити само ако шест година не проговори.',
      'За то време морала је да исплете шест кошуља од оштрих звездастих коприва.',
      'Девојка је живела у шуми, вредно плела и чувала тајну упркос болним рукама.',
      'Млади краљ је пронашао и заволео, иако није могла да му објасни свој задатак.',
      'Злобни људи на двору оптуживали су је јер њено ћутање нису разумели.',
      'Она није прекинула завет чак ни када је дошла у велику опасност.',
      'Последњег дана долетело је шест лабудова, а сестра је бацила кошуље преко њих.',
      'Петорица су потпуно постала људи, док је шестом остао један лабудов рукав.',
      'Истина је откривена, породица се поново окупила, а истрајност је победила чаролију.'
    ],
    lesson: 'љубав, стрпљење и истрајност могу издржати веома тежак задатак',
    question: 'Од чега је сестра плела кошуље?',
    answers: ['Од коприве', 'Од свиле', 'Од облака'],
    correct: 'Од коприве'
  },
  {
    id: 'carobnjakov-ucenik',
    title: 'Чаробњаков ученик',
    art: '🪄 🪣 🌊',
    category: 'Bajke',
    source: goethe,
    plot: [
      'Млади ученик желео је да покаже како већ уме да користи чаролије свога учитеља.',
      'Док је чаробњак био одсутан, оживео је метлу да уместо њега носи воду.',
      'Метла је послушно пунила каду, али ученик није знао речи које је заустављају.',
      'Вода се прелила по поду, низ степенице и у све собе.',
      'У паници је секиром расцепио метлу на два дела.',
      'Обе половине су устале, узеле по ведро и почеле да доносе двоструко више воде.',
      'Ученик је покушавао нове речи, али су погрешне чаролије само повећавале поплаву.',
      'Чаробњак се вратио, изговорио једну тачну заповест и све метле су стале.',
      'Ученик је признао грешку и заједно са учитељем очистио целу радионицу.',
      'Од тада је прво учио правила до краја, а тек онда испробавао нову моћ.'
    ],
    lesson: 'моћ без знања и одговорности лако направи већи проблем',
    question: 'Ко је непрестано доносио воду?',
    answers: ['Оживљена метла', 'Мали змај', 'Бели коњ'],
    correct: 'Оживљена метла'
  },
  {
    id: 'devojcica-sa-sibicama',
    title: 'Девојчица са шибицама',
    art: '🕯️ ❄️ ⭐',
    category: 'Pred spavanje',
    source: andersen,
    plot: [
      'У хладној новогодишњој ноћи сиромашна девојчица покушавала је да прода шибице на улици.',
      'Људи су журили топлим кућама и готово нико није примећивао њене промрзле руке.',
      'Склонила се у угао између зграда и запалила једну шибицу да се мало угреје.',
      'У пламену је видела топлу пећ, али је слика нестала када се шибица угасила.',
      'Друга јој је показала свечану трпезу, а трећа прелепу јелку пуну свећа.',
      'Једна звезда је пала и девојчица се сетила бакиних речи о светлу које одлази на небо.',
      'Запалила је све преостале шибице и угледала вољену баку у снажној светлости.',
      'У машти је бака загрлила и одвела далеко од хладноће и глади.',
      'Следећег јутра пролазници су схватили да су прекасно приметили дете коме је требала помоћ.',
      'Прича нас подсећа да не окрећемо главу од усамљених и да доброту покажемо одмах.'
    ],
    lesson: 'туђу невољу треба приметити и помоћи док још можемо',
    question: 'Кога је девојчица највише желела да поново види?',
    answers: ['Баку', 'Краљицу', 'Учитељицу'],
    correct: 'Баку'
  },
  {
    id: 'palcic',
    title: 'Палчић',
    art: '👦 👢 🏠',
    category: 'Bajke',
    source: perrault,
    plot: [
      'Палчић је био најмлађи од седморице браће и толико ситан да су га сви потцењивали.',
      'Када је породица остала без хране, чуо је разговор родитеља и припремио беле каменчиће.',
      'Каменчићи су први пут довели браћу назад кући кроз мрачну шуму.',
      'Следећег пута имао је само хлебне мрвице, које су птице појеле.',
      'Деца су стигла до куће џина, а његова жена их је сакрила од опасности.',
      'Палчић је током ноћи заменио круне џинових ћерки капицама своје браће.',
      'Када је џин открио превару, обуо је чаробне чизме од седам миља и кренуо у потеру.',
      'Уморан је заспао, па је Палчић узео чизме које су се прилагодиле његовим малим стопалима.',
      'Са њима је постао брзи краљевски гласник и зарадио довољно за целу породицу.',
      'Најмањи брат показао је да пажња и памет нису ствар висине.'
    ],
    lesson: 'нечија величина не одређује његову храброст и способност',
    question: 'Шта је Палчић користио да први пут нађе пут?',
    answers: ['Беле каменчиће', 'Црвене заставице', 'Чаробни компас'],
    correct: 'Беле каменчиће'
  },
  {
    id: 'mala-crvena-koka',
    title: 'Мала црвена кока',
    art: '🐔 🌾 🍞',
    category: 'Životinje',
    source: jacobs,
    plot: [
      'Мала црвена кока пронашла је неколико зрна пшенице у дворишту.',
      'Питала је мачку, пса и патку ко ће јој помоћи да их посади.',
      'Сви су одговорили да неће, па је кока сама припремила земљу и посејала зрна.',
      'Када је пшеница порасла, поново је тражила помоћ за жетву.',
      'Пријатељи су радије одмарали, зато је кока сама пожњела и однела зрно у млин.',
      'Нико није желео да меси тесто, па је она стрпљиво направила хлеб.',
      'Убрзо се двориштем проширио диван мирис свеже печене векне.',
      'Тада су мачка, пас и патка одмах пожелели да помогну у јелу.',
      'Кока је хлеб поделила са пилићима који су јој помагали у малим пословима.',
      'Остали су схватили поруку и следећег дана сами понудили помоћ у башти.'
    ],
    lesson: 'ко учествује у раду, заслужује да дели и његов резултат',
    question: 'Шта је кока направила од пшенице?',
    answers: ['Хлеб', 'Торту од чоколаде', 'Сок'],
    correct: 'Хлеб'
  },
  {
    id: 'ruza-i-snezana',
    title: 'Ружица и Снежана',
    art: '🌹 ❄️ 🐻',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Сестре Ружица и Снежана живеле су са мајком у кући крај две руже, црвене и беле.',
      'Једне зимске вечери на врата је покуцао велики медвед тражећи место поред ватре.',
      'Девојчице су се спријатељиле са нежним гостом који се сваког јутра враћао у шуму.',
      'У пролеће су више пута помагале незахвалном патуљку чија се брада запетљала у дрво и уже.',
      'Патуљак их је сваки пут грдио, иако су га спасле из невоље.',
      'Сестре су откриле да патуљак краде драго камење из планинских пећина.',
      'Када је медвед покушао да га заустави, патуљак му је понудио девојчице у замену за себе.',
      'Медвед је победио похлепног патуљка, а његова тешка кожа пала је на земљу.',
      'Пред њима се појавио принц кога је патуљак раније зачарао и опљачкао.',
      'Породице су постале блиске, а две руже су наставиле да цветају као знак сестринске љубави.'
    ],
    lesson: 'нежност и сестринска слога могу препознати добро испод необичног изгледа',
    question: 'Ко је зими покуцао на врата сестара?',
    answers: ['Медвед', 'Лисица', 'Витез'],
    correct: 'Медвед'
  },
  {
    id: 'dvanaest-igracica',
    title: 'Дванаест принцеза',
    art: '👸 💃 🌙',
    category: 'Bajke',
    source: grimm,
    plot: [
      'Дванаест принцеза сваког јутра имало је потпуно изношене ципеле, иако су врата собе била закључана.',
      'Краљ је обећао награду ономе ко за три ноћи открије њихову тајну.',
      'Млади војник послушао је савет старице и није попио успављујуће вино.',
      'Огрнуо је невидљиви плашт и пратио принцезе кроз тајни пролаз испод кревета.',
      'Прошли су кроз шуме са сребрним, златним и дијамантским лишћем.',
      'На подземном језеру чекало је дванаест чамаца са дванаест принчева.',
      'У дворани су сви плесали до јутра, док им се ципеле нису сасвим истрошиле.',
      'Војник је као доказ понео гранчице и златну чашу, а да га нико није видео.',
      'Трећег дана испричао је краљу све и показао сакупљене предмете.',
      'Принцезе су признале тајну, а тајни пролаз више није коришћен без краљевог знања.'
    ],
    lesson: 'тајна која повређује поверење на крају захтева искрено објашњење',
    question: 'Шта је војника чинило невидљивим?',
    answers: ['Чаробни плашт', 'Сребрне ципеле', 'Златна чаша'],
    correct: 'Чаробни плашт'
  },
  {
    id: 'divlji-labudovi',
    title: 'Дивљи лабудови',
    art: '🦢 👗 🌊',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Принцеза Елиза имала је једанаесторицу браће које је зла краљица претворила у дивље лабудове.',
      'Лабудови су дању летели, а само ноћу поново добијали људски облик.',
      'Елиза их је пронашла на морској обали и заједно су у мрежи прелетели преко мора.',
      'У сну је сазнала да мора исплести једанаест кошуља од коприве и све време ћутати.',
      'Коприва јој је пекла руке, али је наставила због љубави према браћи.',
      'Млади краљ је одвео у свој замак, иако никоме није могла да објасни зашто ноћу сакупља биљке.',
      'Због ћутања су је погрешно оптужили за чаробњаштво.',
      'Елиза је плела до последњег тренутка и бацила кошуље преко лабудова.',
      'Браћа су постала принчеви, само је најмлађем остао један лабудов рукав јер кошуља није била готова.',
      'Истина је спасла Елизу, а њена жртва поново је окупила целу породицу.'
    ],
    lesson: 'истрајна љубав понекад говори најјаче баш онда када морамо да ћутимо',
    question: 'Колико је браће Елиза спасавала?',
    answers: ['Једанаест', 'Тројицу', 'Двадесет'],
    correct: 'Једанаест'
  },
  {
    id: 'ribar-i-zlatna-ribica',
    title: 'Рибар и златна рибица',
    art: '🎣 🐟 ✨',
    category: 'Bajke',
    source: pushkin,
    plot: [
      'Сиромашни рибар ухватио је златну рибицу која је проговорила и замолила да је пусти.',
      'Он ју је без награде вратио у море и код куће испричао необичан догађај.',
      'Његова жена затражила је да рибица најпре замени старо корито новим.',
      'Када се жеља испунила, пожелела је кућу, затим дворац и све већу власт.',
      'Море је са сваким новим захтевом постајало тамније и немирније.',
      'Рибар је био захвалан за довољно, али је из страха настављао да преноси њене жеље.',
      'Жена је на крају захтевала да влада морем и самом златном рибицом.',
      'Рибица овога пута није ништа рекла, већ је нестала испод великих таласа.',
      'Када се рибар вратио, дворац је нестао, а испред колибе стајало је старо корито.',
      'Обоје су научили да незахвална похлепа може однети и оно добро што већ имамо.'
    ],
    lesson: 'захвалност чува радост, док похлепа никада не зна када је доста',
    question: 'Шта је рибар урадио са златном рибицом?',
    answers: ['Пустио ју је у море', 'Ставио је у акваријум', 'Однео је краљу'],
    correct: 'Пустио ју је у море'
  },
  {
    id: 'ivica-i-carobna-frula',
    title: 'Чаробна фрула',
    art: '🎶 🐀 🏘️',
    category: 'Bajke',
    source: browning,
    plot: [
      'Град су преплавили пацови, а становници више нису могли да сачувају храну.',
      'Тајанствени свирач понудио је помоћ ако му градоначелник исплати договорену награду.',
      'Засвирао је фрулу, па су сви пацови у колони кренули за мелодијом изван града.',
      'Свирач их је одвео далеко преко реке и град је поново постао чист.',
      'Градоначелник је прекршио обећање и одбио да плати договорени износ.',
      'Свирач је упозорио да непоштена реч може имати последице, али су му се одрасли смејали.',
      'Поново је засвирао, а овога пута су за музиком кренула деца жељна игре.',
      'Једно дете које није могло брзо да хода вратило се и објаснило шта се десило.',
      'Уплашени одрасли пронашли су свирача, извинили се и испунили обећање.',
      'Деца су се безбедно вратила, а град је увео правило да се сваки поштен договор мора поштовати.'
    ],
    lesson: 'дата реч и поштен договор морају се поштовати',
    question: 'Чиме је свирач повео пацове?',
    answers: ['Мелодијом фруле', 'Златним звоном', 'Колачима'],
    correct: 'Мелодијом фруле'
  },
  {
    id: 'kraljica-pcela',
    title: 'Краљица пчела',
    art: '🐝 👑 🐜',
    category: 'Životinje',
    source: grimm,
    plot: [
      'Три краљевска брата кренула су у свет, али су двојица старијих желела само забаву.',
      'Најмлађи је спречио браћу да униште мравињак, уплаше патке и запале пчелињу кошницу.',
      'Стигли су у зачарани дворац где су људи и животиње били претворени у камен.',
      'Да би разбили чаролију, морали су да испуне три готово немогућа задатка.',
      'Мрави су најмлађем помогли да сакупи хиљаду расутих бисера из маховине.',
      'Патке су зарониле у језеро и донеле кључ скривен дубоко под водом.',
      'Требало је препознати најмлађу принцезу међу три исте сестре.',
      'Краљица пчела слетела је на усне девојке која је јела мед и показала прави избор.',
      'Чаролија је нестала, дворјани су оживели, а старија браћа су схватила своју грешку.',
      'Најмлађи је успео зато што је раније показао милост према сваком малом створењу.'
    ],
    lesson: 'доброта према природи враћа се онда када нам је помоћ најпотребнија',
    question: 'Ко је показао која је најмлађа принцеза?',
    answers: ['Краљица пчела', 'Бела патка', 'Најстарији брат'],
    correct: 'Краљица пчела'
  },
  {
    id: 'leteci-kofer',
    title: 'Летећи кофер',
    art: '🧳 ☁️ 🏰',
    category: 'Bajke',
    source: andersen,
    plot: [
      'Млади трговац потрошио је наследство и на крају му је остао само необичан стари кофер.',
      'Када је сео у њега, кофер је полетео изнад кровова и однео га у далеку земљу.',
      'У кули је живела принцеза којој родитељи нису дозвољавали да упозна путнике.',
      'Трговац се представио као небески гласник и освојио је причама.',
      'Краљ и краљица затражили су да им исприча причу која ће истовремено бити смешна и мудра.',
      'Он је измислио повест о шибицама, лонцима и тањирима који су се понашали као људи.',
      'Двор се одушевио и одредио дан прославе веридбе.',
      'Трговац је из летећег кофера приредио ватромет изнад града.',
      'Једна варница пала је на кофер и спалила га, па више није могао да стигне до куле.',
      'Принцеза је чекала, а трговац је наставио да путује пешке и прича своје чудесне приче.'
    ],
    lesson: 'машта је драгоцена, али обећања траже и поуздан план',
    question: 'Како је трговац стигао до куле?',
    answers: ['Летећим кофером', 'Белим коњем', 'Подземним пролазом'],
    correct: 'Летећим кофером'
  }
];

function buildTale(
  seed: ClassicTaleSeed,
  age: FairyTaleAge,
  useFullSourceContent: boolean
): FairyTale {
  const hasFullContent = useFullSourceContent && hasFullStoryContent(seed.id);
  const pages = hasFullContent
    ? [['Цела изворно проверена прича се учитава…']]
    : seed.plot.map((event) => [event]);
  return {
    id: `${seed.id}-${age.replace('–', '-')}`,
    age,
    category: seed.category,
    title: seed.title,
    art: seed.art,
    pages,
    sentences: pages.flat(),
    question: seed.question,
    answers: seed.answers,
    correct: seed.correct,
    audioKey: `${seed.id}-${hasFullContent ? 'full' : 'sazeta'}`,
    // Prodavničko izdanje koristi bezbedne, uzrasno prilagođene tekstove,
    // ali za njih postoje provereni lokalni `*-sazeta-*.mp3` snimci. Ranije
    // `false` je ostavljao Android/iOS build bez naratora iako su MP3 fajlovi
    // bili spakovani uz aplikaciju.
    recordedAudio: hasFullContent ? (seed.recordedAudio ?? true) : !useFullSourceContent,
    recordedVoice: 'sr-RS-SophieNeural',
    plotKey: seed.id,
    source: seed.source,
    adaptation: 'originalna-srpska-adaptacija',
    edition: hasFullContent ? 'complete' : 'abridged',
    narration: 'audio-first',
    reviewed: hasFullContent,
    fullContentAvailable: hasFullContent
  };
}

export const fairyTaleAges: FairyTaleAge[] = ['4–6', '7–10'];
const storyArt = ['📖 ✨ 🌙', '🏰 🌟 📚', '🌲 🧚 🦉', '👑 🐉 🗝️'];
const publishedStories: ClassicTaleSeed[] = fullStoryContentCatalog.map((entry, index, catalog) => {
  const otherTitles = [
    catalog[(index + 1) % catalog.length].title,
    catalog[(index + 2) % catalog.length].title
  ];
  return {
    id: entry.id,
    title: entry.title,
    art: storyArt[index % storyArt.length],
    category: index % 5 === 1 ? 'Pred spavanje' : (index % 5 === 2 ? 'Životinje' : 'Bajke'),
    source: {
      author: entry.author,
      work: entry.title,
      url: entry.sourceUrl,
      publicDomain: true
    },
    plot: Array.from({ length: 10 }, () => 'Цела изворно проверена прича се учитава…') as ClassicTaleSeed['plot'],
    lesson: 'пажљиво слушање и разговор помажу детету да разуме причу',
    question: 'Коју си причу управо слушао?',
    answers: [entry.title, otherTitles[0], otherTitles[1]],
    correct: entry.title,
    recordedAudio: entry.audioAvailable
  };
});

export type FairyTaleCatalogMode = 'full-source' | 'store-safe';

/**
 * Web izdanje čuva javnodomenske izvorne tekstove. Prodavničko izdanje koristi
 * posebno napisane, uzrasno prilagođene verzije i nikada ne pokazuje niti
 * učitava izvorne JSON/audio pakete.
 */
export function createFairyTaleCatalog(mode: FairyTaleCatalogMode): FairyTale[] {
  const useFullSourceContent = mode === 'full-source';
  const seeds = useFullSourceContent ? publishedStories : classics;

  return fairyTaleAges.flatMap((age) =>
    seeds.map((seed) => buildTale(seed, age, useFullSourceContent))
  );
}

export const storeSafeContentEnabled =
  import.meta.env.VITE_STORE_SAFE_CONTENT === 'true';

export const fairyTales: FairyTale[] = createFairyTaleCatalog(
  storeSafeContentEnabled ? 'store-safe' : 'full-source'
);
